import React from 'react'
import { put } from 'helpers/http-service'
import {
  ITableViewWithColumns,
  ITableRow,
  ITableViewColumn,
  SelectKindOptions,
  EditorContent,
  IContext,
  IColumnAlterOptions,
  ITable,
  ICreateTableJoin,
  IUpdateTableViewCell,
  ICellValue
} from 'types'
import {
  ISpreadsheetData,
  SpreadsheetReducerActions,
  IColumnTypes,
  ITableViewHistoryCell,
  ISelectedCellState,
  ISelectedCellRange
} from 'components/spreadsheet/types'
import {
  convertToApiFilter,
  convertToApiColour,
  convertToApiRowLock,
  convertToApiChart,
  sortRows,
  getRowValue,
  rowDataArrayToObject,
  convertRowDataToNameValueMap,
  getOriginalData
} from 'components/spreadsheet/helpers/functions'
import {
  SELECT_COLUMN_TYPES,
  ViewTypes,
  ViewTypesNames,
  hasSyntheticColumns
} from 'components/spreadsheet/constants/const'
import { withBusyOverlay, runSpreadsheetMutation } from 'components/spreadsheet/helpers/busy-overlay'
import api from 'helpers/api'

/** Returns the persistable columns for a view, using original columns for synthetic-column views. */
const getSaveColumns = (spreadsheetData: ISpreadsheetData): ITableViewColumn[] =>
  getOriginalData(spreadsheetData).columns

// A column to create, which can carry a select's options (the create endpoint saves them with the column, so a list
// it rejects fails the whole create rather than leaving a column without its options).
export type ICreateTableColumnView = Omit<ITableViewColumn, 'publicId' | 'kindOptions' | 'isSynced'> &
  IContext & { kindOptions?: SelectKindOptions }

export const createTableColumnViewAction = (
  tableViewId: string,
  column: ICreateTableColumnView,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: (error: any) => void
) => {
  withBusyOverlay(setSpreadsheetData, 'Adding column…', () =>
    api
      .createTableColumn(tableViewId, column)
      .then((response) => {
        setSpreadsheetData({ type: 'ADD_COLUMN', column: response.data as ITableViewColumn })
        onSuccess()
      })
      .catch((error) => {
        onFailure(error)
      })
  )
}

export const deleteTableColumnAction = (
  tableId: string,
  columnId: string,
  projectId: string,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: (error: any) => void
) => {
  withBusyOverlay(setSpreadsheetData, 'Deleting column…', () =>
    api
      .deleteTableColumn(tableId, columnId, projectId)
      .then(() => {
        setSpreadsheetData({ type: 'DELETE_COLUMN', columnId })
        onSuccess()
      })
      .catch((error) => {
        onFailure(error)
      })
  )
}

/** Delete several columns in one go, behind a single busy overlay. There is no bulk delete-columns
 *  endpoint, so the API calls are made per column; but the grid is updated with a single atomic
 *  DELETE_COLUMNS dispatch for whatever was actually removed server-side (one re-index + rowData
 *  pass instead of one per column). A mid-run failure still applies the columns deleted so far. */
export const deleteTableColumnsAction = async (
  tableId: string,
  columnIds: string[],
  projectId: string,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: (error: any) => void
): Promise<void> => {
  const label = columnIds.length === 1 ? 'Deleting column…' : 'Deleting columns…'
  const deletedIds: string[] = []
  try {
    await withBusyOverlay(setSpreadsheetData, label, async () => {
      for (const columnId of columnIds) {
        await api.deleteTableColumn(tableId, columnId, projectId)
        deletedIds.push(columnId)
      }
    })
  } catch (error) {
    if (deletedIds.length > 0) setSpreadsheetData({ type: 'DELETE_COLUMNS', columnIds: deletedIds })
    onFailure(error)
    return
  }
  setSpreadsheetData({ type: 'DELETE_COLUMNS', columnIds: deletedIds })
  onSuccess()
}

export const deleteTableRowAction = (
  rowIds: string[],
  projectId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: (error: unknown) => void
) => {
  const label = rowIds.length === 1 ? 'Deleting row…' : 'Deleting rows…'
  runSpreadsheetMutation(setSpreadsheetData, label, spreadsheetData.totalRows, () =>
    api
      .deleteTableRows(
        spreadsheetData.activeTableView,
        rowIds,
        projectId,
        spreadsheetData.processId,
        spreadsheetData.processSectionId
      )
      .then(() => {
        setSpreadsheetData({ type: 'DELETE_ROWS', rowIds })
        onSuccess()
      })
      .catch((error) => {
        onFailure(error)
      })
  )
}

export interface ITableViewHistory extends IContext {
  type: 'EDIT_CELL' | 'DELETE_ROWS' | 'ADD_ROW'
  cells?: ITableViewHistoryCell[]
  prevSelectedCell?: ISelectedCellState
  prevSelectedCellRange?: ISelectedCellRange
  nextSelectedCell?: ISelectedCellState
  nextSelectedCellRange?: ISelectedCellRange
}

export const updateTableViewCellsAction = (
  tableViewId: string,
  updateTableViewPayload: IUpdateTableViewCell,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onError: (error: any) => void,
  ignoreHistory?: boolean,
  selectionSnapshot?: {
    prevSelectedCell?: ISelectedCellState
    prevSelectedCellRange?: ISelectedCellRange
    nextSelectedCell?: ISelectedCellState
    nextSelectedCellRange?: ISelectedCellRange
  }
): void => {
  // Remap columnId to columnName for the API
  const newHistory: ITableViewHistory = {
    type: 'EDIT_CELL',
    cells: [],
    context: updateTableViewPayload.context,
    prevSelectedCell: selectionSnapshot?.prevSelectedCell,
    prevSelectedCellRange: selectionSnapshot?.prevSelectedCellRange,
    nextSelectedCell: selectionSnapshot?.nextSelectedCell,
    nextSelectedCellRange: selectionSnapshot?.nextSelectedCellRange
  }
  let errorCells = null
  const remappedCells = updateTableViewPayload.cells.map((cell) => {
    const row = spreadsheetData.rows.find((row) => row.publicId === cell.rowId)
    const previousValue = row
      ? getRowValue(row, cell.columnId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
      : undefined
    const column = spreadsheetData.viewDetails.columns.find((c) => c.publicId === cell.columnId)

    if (!column) {
      errorCells = cell.columnId
      return
    }
    const newCell = {
      ...cell,
      columnName: column.name
    }
    if (!ignoreHistory && previousValue !== undefined && newHistory.cells) {
      newHistory['cells'].push({
        rowId: cell.rowId,
        columnId: cell.columnId,
        oldValue: previousValue,
        newValue: cell.value
      })
    }
    return {
      columnName: newCell.columnName,
      rowId: newCell.rowId,
      value: newCell.value,
      processPublicId: newCell.processPublicId,
      processSectionPublicId: newCell.processSectionPublicId
    }
  })

  if (errorCells) {
    onError('A column you changed is no longer in this view. Refresh the page and try again.')
    return
  }

  // Add in process context
  const processContext = spreadsheetData.processId
    ? {
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId!
      }
    : {}

  const payload = {
    ...updateTableViewPayload,
    cells: remappedCells,
    context: { ...updateTableViewPayload.context, ...processContext }
  }

  api({ method: 'PUT', endpoint: `/table/views/${tableViewId}/cells`, data: payload })
    .then((response) => {
      const data = response.data as ITableRow[]

      const cellsToUpdate = []
      for (const row of data) {
        // API returns rowData with column names as keys
        const apiRowData = row.rowData as unknown as Record<string, ICellValue>
        for (const column of spreadsheetData.viewDetails.columns) {
          if (column.formulaEnabled || column.scriptEnabled || column.isJoined) {
            cellsToUpdate.push({
              rowId: row.publicId,
              columnId: column.publicId,
              value: {
                [column.publicId]: apiRowData[column.name]
              }
            })
          }
        }
      }

      setSpreadsheetData({
        type: 'BULK_EDIT_CELL',
        cells: cellsToUpdate
      })

      onSuccess()

      // Add history
      if (newHistory.cells && newHistory.cells.length > 0) {
        setSpreadsheetData({
          type: 'ADD_HISTORY',
          event: newHistory
        })
      }
    })
    .catch((error) => {
      onError(error)

      // Revert the edit cell action
      if (newHistory.cells && newHistory.cells.length > 0) {
        for (const history of newHistory.cells) {
          setSpreadsheetData({
            type: 'EDIT_CELL',
            rowId: history.rowId,
            columnId: history.columnId,
            value: { [history.columnId]: history.oldValue as string },
            oldValue: history.newValue as string
          })
        }
      }
    })
}

export const addNewView = (
  projectId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  type: number,
  configurationParentId: string | null,
  onSuccess: (newViewId: string) => void,
  onFailure: (error: any) => void,
  inProcess: boolean,
  onFinally?: () => void
) => {
  const name = `New ${ViewTypesNames[type]} View`
  const newViewIsSynthetic = hasSyntheticColumns(type)
  const currentViewIsSynthetic = hasSyntheticColumns(spreadsheetData.viewDetails.type)
  // Grouping is meaningless on synthetic views — the rendered rows/columns are not the
  // originals, so a "grouped column" cannot resolve. Drop group-related settings when the
  // NEW view is synthetic regardless of the source view.
  const skipGroupSettings = newViewIsSynthetic
  // Row-lock rules are per-view: a new shared view (spreadsheet, transpose, matrix)
  // starts with a copy of the source's rules and owns them independently. Personal
  // views are the one exception — they inherit their configuration parent's rules
  // live, so copying here would make them own duplicates of what they already
  // inherit (the lock menu then shows each rule twice).
  const skipRowLockSettings = Boolean(configurationParentId)

  const newViewPayload = {
    name,
    type,
    description: spreadsheetData.viewDetails.description,
    filterSettings: convertToApiFilter(spreadsheetData.userConfiguration.filterSettings),
    groupSettings: skipGroupSettings ? [] : spreadsheetData.userConfiguration.groupSettings,
    sortSettings: spreadsheetData.userConfiguration.sortSettings,
    colourSettings: convertToApiColour(spreadsheetData.userConfiguration.colourSettings),
    rowLockSettings: skipRowLockSettings ? [] : convertToApiRowLock(spreadsheetData.userConfiguration.rowLockSettings),
    chartSettings: convertToApiChart(spreadsheetData.userConfiguration.chartSettings),
    transposeSettings: spreadsheetData.viewDetails.transposeSettings,
    matrixSettings: spreadsheetData.viewDetails.matrixSettings,
    calendarSettings: spreadsheetData.userConfiguration.calendarSettings,
    frozenIndex: newViewIsSynthetic ? 0 : currentViewIsSynthetic ? 0 : spreadsheetData.viewDetails.frozenIndex,
    rowHeight: spreadsheetData.viewDetails.rowHeight,
    disableNewRow: newViewIsSynthetic ? true : spreadsheetData.viewDetails.disableNewRow,
    disableSyncCsv: spreadsheetData.viewDetails.disableSyncCsv,
    allowContributorDelete: spreadsheetData.viewDetails.allowContributorDelete,
    displayValidationErrorRows: spreadsheetData.viewDetails.displayValidationErrorRows,
    displayCommentRows: spreadsheetData.viewDetails.displayCommentRows,
    collapsedGroupView: skipGroupSettings ? false : spreadsheetData.viewDetails.collapsedGroupView,
    unpackMultiselectGroupView: skipGroupSettings ? false : spreadsheetData.viewDetails.unpackMultiselectGroupView,
    treeGroupView: skipGroupSettings ? false : spreadsheetData.viewDetails.treeGroupView,
    includeAllColumns: false,
    columns: getSaveColumns(spreadsheetData)
      .filter((column: ITableViewColumn) => !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId))
      .map((column) => {
        const newColumn = JSON.parse(JSON.stringify(column))
        if (newColumn['required'] === undefined) newColumn['required'] = false
        if (SELECT_COLUMN_TYPES.includes(newColumn.kind) && newColumn.kindOptions?.tableOptions)
          delete newColumn.kindOptions?.tableOptions['cachedOptions']

        return {
          columnId: column.publicId,
          columnName: column.name,
          required: column.required,
          description: column.description,
          stringValidation: column.stringValidation,
          validationMessage: column.validationMessage,
          hardValidation: column.hardValidation,
          validationNoBlanks: column.validationNoBlanks,
          validationNoDuplicates: column.validationNoDuplicates,
          displayValidationError: column.displayValidationError,
          blockInvalidSelectValues: column.blockInvalidSelectValues,
          formWidth: column.formWidth
        }
      }),
    ...(configurationParentId && { configurationParentId }),
    context: { projectId }
  }

  // A Custom View starts with every table column and nothing else from the source view
  // (no filters, sorts or other settings); the author narrows it from the toolbar.
  const payload =
    type === ViewTypes.CUSTOM_VIEW
      ? { name, type, includeAllColumns: true, columns: [], context: { projectId } }
      : newViewPayload

  api
    .createNewView(spreadsheetData.tableDetails.publicId, payload)
    .then((response) => {
      const viewData = response.data as ITableViewWithColumns
      setSpreadsheetData({
        type: 'ADD_VIEW',
        view: viewData,
        inProcess
      })
      onSuccess(response.data.publicId)
    })
    .catch((error) => {
      onFailure(error)
    })
    .finally(() => {
      if (onFinally) {
        onFinally()
      }
    })
}

/** Copies a Form view as a Custom View that starts as the same form, and adds it to the table's views. */
export const copyAsCustomView = (
  projectId: string,
  viewId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: (newViewId: string) => void,
  onFailure: (error: unknown) => void
): void => {
  api
    .copyAsCustomView(spreadsheetData.tableDetails.publicId, viewId, { context: { projectId } })
    .then((response) => {
      // Never inProcess: the menu item isn't offered inside a document.
      setSpreadsheetData({ type: 'ADD_VIEW', view: response.data, inProcess: false })
      onSuccess(response.data.publicId)
    })
    .catch(onFailure)
}

export const saveCurrentView = (
  projectId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: (error: any) => void
) => {
  const newTableView: Partial<ITableViewWithColumns> & IContext = {
    name: spreadsheetData.viewDetails.name,
    type: spreadsheetData.viewDetails.type,
    description: spreadsheetData.viewDetails.description,
    filterSettings: convertToApiFilter(spreadsheetData.userConfiguration.filterSettings),
    groupSettings: spreadsheetData.userConfiguration.groupSettings,
    sortSettings: spreadsheetData.userConfiguration.sortSettings,
    colourSettings: convertToApiColour(spreadsheetData.userConfiguration.colourSettings),
    rowLockSettings: convertToApiRowLock(spreadsheetData.userConfiguration.rowLockSettings),
    chartSettings: convertToApiChart(spreadsheetData.userConfiguration.chartSettings),
    calendarSettings: spreadsheetData.userConfiguration.calendarSettings,
    frozenIndex: spreadsheetData.viewDetails.frozenIndex,
    displayValidationErrorRows: spreadsheetData.viewDetails.displayValidationErrorRows,
    displayCommentRows: spreadsheetData.viewDetails.displayCommentRows,
    collapsedGroupView: spreadsheetData.viewDetails.collapsedGroupView,
    unpackMultiselectGroupView: spreadsheetData.viewDetails.unpackMultiselectGroupView,
    treeGroupView: spreadsheetData.viewDetails.treeGroupView,
    rowHeight: spreadsheetData.viewDetails.rowHeight,
    transposeSettings: spreadsheetData.viewDetails.transposeSettings,
    matrixSettings: spreadsheetData.viewDetails.matrixSettings,
    columns: getSaveColumns(spreadsheetData)
      .map((column) => {
        const newColumn = JSON.parse(JSON.stringify(column))
        if (newColumn['required'] === undefined) newColumn['required'] = false
        if (SELECT_COLUMN_TYPES.includes(newColumn.kind) && newColumn.kindOptions?.tableOptions)
          delete newColumn.kindOptions?.tableOptions['cachedOptions']

        return newColumn
      })
      .filter((column: ITableViewColumn) => !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId)),
    context: { projectId }
  }

  api
    .updateWholeTableView(spreadsheetData.activeTableView, newTableView)
    .then(() => {
      setSpreadsheetData({ type: 'SAVE_CURRENT_VIEW' })
      onSuccess()
    })
    .catch((error) => {
      onFailure(error)
    })
}

export const updateTable = (
  field:
    | 'name'
    | 'logo'
    | 'keepValidationsInSync'
    | 'keepColoursInSync'
    | 'isDeleted'
    | 'syncHourlyFrequency'
    | 'allowComments',
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  value: string | string[] | boolean | number,
  projectId: string,
  onSuccess: (updatedTable: ITable) => void,
  onError: (message: string) => void
) => {
  let name = spreadsheetData.tableDetails.name
  let keepValidationsInSync = spreadsheetData.tableDetails.keepValidationsInSync
  let keepColoursInSync = spreadsheetData.tableDetails.keepColoursInSync
  let isDeleted = spreadsheetData.tableDetails.isDeleted
  let deletedAt = spreadsheetData.tableDetails.deletedAt
  let syncHourlyFrequency = spreadsheetData.tableDetails.syncHourlyFrequency
  let allowComments = spreadsheetData.tableDetails.allowComments

  if (field === 'name' && typeof value === 'string') name = value
  else if (field === 'keepValidationsInSync' && typeof value === 'boolean') keepValidationsInSync = value
  else if (field === 'keepColoursInSync' && typeof value === 'boolean') keepColoursInSync = value
  else if (field === 'isDeleted' && typeof value === 'boolean') {
    if (isDeleted === true && value === false) {
      deletedAt = null
      isDeleted = false
    } else if (isDeleted === false && value === true) {
      deletedAt = new Date().toISOString()
      isDeleted = true
    }
  } else if (field === 'syncHourlyFrequency' && typeof value === 'number') syncHourlyFrequency = value
  else if (field === 'allowComments' && typeof value === 'boolean') allowComments = value

  api
    .updateTable(spreadsheetData.tableDetails.publicId, {
      name,
      keepValidationsInSync,
      keepColoursInSync,
      syncHourlyFrequency,
      allowComments,
      context: { projectId: projectId }
    })
    .then((response) => {
      const tableUpdated = response.data
      if (field === 'isDeleted') {
        setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'deletedAt', value: deletedAt })
        setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isDeleted', value: isDeleted })
      } else {
        setSpreadsheetData({ type: 'UPDATE_TABLE', field, value })
      }

      onSuccess(tableUpdated)
    })
    .catch((error) => {
      onError(error)
    })
}

export const createTableJoin = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  tableJoin: ICreateTableJoin,
  projectId: string,
  onSuccess: () => void,
  onError: (error: any) => void
) => {
  api
    .createTableJoin(spreadsheetData.tableDetails.publicId, projectId, tableJoin)
    .then((response) => {
      setSpreadsheetData({ type: 'UPDATE_TABLE_JOINS', table: response.data as ITable })
      onSuccess()
    })
    .catch((error) => {
      onError(error)
    })
}

export const updateTableJoin = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  tableJoinId: string,
  tableJoin: ICreateTableJoin,
  projectId: string,
  onSuccess: () => void,
  onError: (error: any) => void
) => {
  api
    .updateTableJoin(spreadsheetData.tableDetails.publicId, tableJoinId, projectId, tableJoin)
    .then((response) => {
      setSpreadsheetData({ type: 'UPDATE_TABLE_JOINS', table: response.data })
      onSuccess()
    })
    .catch((error) => {
      onError(error)
    })
}

export const deleteTableJoin = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  tableJoinId: string,
  projectId: string,
  onSuccess: () => void,
  onError: (error: any) => void
) => {
  api
    .deleteTableJoin(spreadsheetData.tableDetails.publicId, tableJoinId, projectId)
    .then((response) => {
      setSpreadsheetData({ type: 'UPDATE_TABLE_JOINS', table: response.data })
      onSuccess()
    })
    .catch((error) => {
      onError(error)
    })
}

export const deleteTable = (
  projectId: string,
  spreadsheetData: ISpreadsheetData,
  onSuccess: () => void,
  onFailure: (error: any) => void
) => {
  api
    .deleteTable(spreadsheetData.tableDetails.publicId, { context: { projectId } })
    .then(() => {
      onSuccess()
    })
    .catch((error) => {
      onFailure(error)
    })
}

export const updateView = (
  field:
    | 'name'
    | 'description'
    | 'type'
    | 'disableNewRow'
    | 'disableSyncCsv'
    | 'allowContributorDelete'
    | 'displayValidationErrorRows'
    | 'displayCommentRows'
    | 'collapsedGroupView'
    | 'unpackMultiselectGroupView',
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  viewId: string,
  value: string | EditorContent | null | number | boolean,
  projectId: string,
  onSuccess: () => void,
  onFailure: (error: string) => void
) => {
  api
    .updateTableView(viewId, projectId, field, value)
    .then(() => {
      setSpreadsheetData({ type: 'UPDATE_VIEW', field, viewId, value })
      onSuccess()
    })
    .catch((error) => {
      onFailure(error)
    })
}

export const deleteView = (
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  projectId: string,
  viewId: string,
  onSuccess: () => void,
  onFailure: (error: string) => void
) => {
  api
    .deleteView(viewId, projectId)
    .then(() => {
      setSpreadsheetData({ type: 'DELETE_VIEW', viewId })
      onSuccess()
    })
    .catch((error) => {
      onFailure(error)
    })
}

export const updateColumn = (
  projectId: string,
  field:
    | 'name'
    | 'description'
    | 'kind'
    | 'kindOptions'
    | 'script'
    | 'scriptEnabled'
    | 'formula'
    | 'formulaEnabled'
    | 'aggregate'
    | 'stringValidation'
    | 'hardValidation'
    | 'validationMessage'
    | 'validationNoBlanks'
    | 'validationNoDuplicates'
    | 'width'
    | 'thousandSeparator'
    | 'decimalPlaces'
    | 'dateFormat'
    | 'exportWidth'
    | 'displayLink'
    | 'displayValidationError'
    | 'blockInvalidSelectValues'
    | 'formWidth'
    | 'defaultValue',
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  columnId: string,
  value: string | number | EditorContent | null | IColumnTypes | SelectKindOptions | boolean,
  onSuccess: () => void,
  onError: (error: any) => void,
  alterOptions?: IColumnAlterOptions
) => {
  let column: Partial<ITableViewColumn> & IContext & Partial<{ alterOptions: IColumnAlterOptions }> = {
    publicId: columnId,
    [field]: value,
    context: { projectId: projectId }
  }

  if (field === 'kind' && (value === 'select' || value === 'multiselect')) {
    const foundColumn = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
    const prevKind = foundColumn?.kind
    if (foundColumn && (prevKind === 'select' || prevKind === 'multiselect')) {
      if (foundColumn.kindOptions?.tableOptions.cachedOptions) {
        delete foundColumn.kindOptions?.tableOptions['cachedOptions']
      }
      column = { ...column, kindOptions: foundColumn.kindOptions }
    }
  }
  // Note: kindOptions updates intentionally do NOT echo the column's `kind` back
  // to the API. The backend preserves the existing kind when it isn't supplied
  // (see update_column_model: `table_column.kind = data.get("kind", table_column.kind)`).
  // Sending `kind` here would re-apply a stale value from spreadsheetData when
  // the kindOptions update runs immediately after a kind change in the same
  // batch (handleUpdateColumns iterates updates from update_columns), reverting
  // the new kind back to the previous one. See issue #9609.
  if (alterOptions) {
    column = {
      ...column,
      alterOptions
    }
  }
  if (field === 'formula') {
    // Also set formulaEnabled to true
    column = {
      ...column,
      formulaEnabled: true
    }
  }

  sendColumnUpdate(spreadsheetData, setSpreadsheetData, columnId, column, onSuccess, onError)
}

// A column's changed fields sent, and the saved column put in the view as the server returns it.
const sendColumnUpdate = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  columnId: string,
  column: Partial<ITableViewColumn> & IContext & Partial<{ alterOptions: IColumnAlterOptions }>,
  onSuccess: () => void,
  onError: (error: any) => void
) => {
  api
    .updateTableColumn(spreadsheetData.activeTableView, columnId, column)
    .then((response) => {
      setSpreadsheetData({ type: 'UPDATE_COLUMN', column: response.data })
      onSuccess()
    })
    .catch((error) => {
      onError(error)
    })
}

/**
 * Several plain fields of a column saved in one request, so they're saved together or not at all (a header's
 * background and text colours, put back by Reset). For fields that need updateColumn's special handling (kind,
 * kindOptions, formula), use that.
 */
export const updateColumnFields = (
  projectId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  columnId: string,
  fields: Partial<Pick<ITableViewColumn, 'headerBackgroundColor' | 'headerTextColor'>>,
  onSuccess: () => void,
  onError: (error: any) => void
) =>
  sendColumnUpdate(
    spreadsheetData,
    setSpreadsheetData,
    columnId,
    { publicId: columnId, ...fields, context: { projectId } },
    onSuccess,
    onError
  )

export const updateRowPosition = (
  projectId: string,
  rowId: string,
  nextRowPublicId: string | null,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  onSuccess: () => void,
  onFailure: () => void,
  sortOrder?: number
) => {
  const row = spreadsheetData.rows.find((row: ITableRow) => row.publicId === rowId)

  if (row && !spreadsheetData.loading) {
    const rows = spreadsheetData.rows.filter((r) => r.publicId !== row.publicId)
    if (rows.length === 0) {
      return
    }

    // Convert array-based rowData to object with column names as keys for the API
    const rowDataAsIdMap = rowDataArrayToObject(
      row.rowData,
      spreadsheetData.indexToColumnId,
      row._rowIndex,
      spreadsheetData.columnStore
    )
    const rowDataAsNameMap = convertRowDataToNameValueMap(spreadsheetData.viewDetails.columns, rowDataAsIdMap)
    // Filter to only include columns that are part of the original view
    const filteredRowData = Object.fromEntries(
      Object.entries(rowDataAsNameMap).filter(([k]) => spreadsheetData.originalViewColumns.includes(k))
    )

    put(`/table/views/${spreadsheetData.viewDetails.publicId}/rows`, {
      rows: [
        {
          publicId: rowId,
          rowData: filteredRowData,
          ...(sortOrder !== undefined ? { sortOrder } : {})
        }
      ],
      nextRowPublicId,
      context: {
        projectId: projectId,
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId
      }
    })
      .then((data) => {
        const newRows = data.data as ITableRow[]

        // Update spreadsheet rows with new rows
        const updatedRows = spreadsheetData.rows.map((row) => {
          const updatedRow = newRows.find((r) => r.publicId === row.publicId)
          if (updatedRow) {
            return { ...row, sortOrder: updatedRow.sortOrder }
          }
          return row
        })

        setSpreadsheetData({
          type: 'SET_SPREADSHEET_ROWS',
          rows: sortRows(
            updatedRows,
            spreadsheetData.userConfiguration.sortSettings,
            spreadsheetData.viewDetails.columns,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.columnStore
          ),
          totalRows: spreadsheetData.totalRows
        })
        onSuccess()
      })
      .catch(() => {
        onFailure()
      })
  } else {
    onFailure()
  }
}

export const refreshSelectOptions = async (
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  spreadsheetData: ISpreadsheetData
) => {
  const hasDependency = spreadsheetData.viewDetails.columns.find(
    (column) =>
      column.kind === 'select' &&
      column.kindOptions?.tableOptions.dependencies &&
      column.kindOptions?.tableOptions.dependencies.length > 0
  )
  if (hasDependency) {
    const response = await api.getTableView(spreadsheetData.activeTableView)
    setSpreadsheetData({ type: 'REFRESH_SELECT_OPTIONS', view: response.data })
  }
}
